import { useTheme } from "@mui/material";
import React, { useCallback, useMemo, useState } from "react";
import { ContainerCard, CustomTabs, LineChart } from "../../../../components";
import { useIsMobile } from "../../../../hooks/useIsMobile";
import { useLocalization } from "../../../../hooks/useLocalization";
import { formatDateYMD } from "../../../../shared/utils/date";
import { ActivityLineChartDataKey } from "../../utils/chartDataUtils";
import {
  ACTIVITY_TREND_METRIC_KEYS,
  carryForwardActivityTrendGaps,
  DEFAULT_ACTIVITY_TREND_METRIC,
  plotUnreportedMetricsAtRangeStart,
  type ActivityTrendDataPoint,
  type ActivityTrendMetricKey,
} from "../../utils/trendChartDataUtils";
import { exportActivityChartCsv } from "../utils/exportCsv";
import { DataAnalyticsChartSkeleton } from "./DataAnalyticsChartSkeleton";

const MIN_Y_AXIS_VALUE = 5;
const TAB_MIN_WIDTH = "143px";

export interface ActivityTrendChartProps {
  data: ActivityTrendDataPoint[];
  startDate: Date;
  endDate: Date;
  selectedLicenseNames?: string[];
  isLoading: boolean;
  isFetching?: boolean;
  isUserLevelDataEnabled?: boolean;
}

export const ActivityTrendChart: React.FC<ActivityTrendChartProps> = ({
  data,
  startDate,
  endDate,
  selectedLicenseNames = [],
  isLoading,
  isFetching = false,
  isUserLevelDataEnabled = false,
}) => {
  const theme = useTheme();
  const { t } = useLocalization();
  const isMobile = useIsMobile();

  const [activeMetric, setActiveMetric] = useState<ActivityTrendMetricKey>(
    DEFAULT_ACTIVITY_TREND_METRIC,
  );

  const metricLabels: Record<ActivityTrendMetricKey, string> = {
    [ActivityLineChartDataKey.UserSessions]: t(
      "pages.dataAnalytics.internalUsage.activity.metrics.userSessions",
    ),
    [ActivityLineChartDataKey.HoursInVr]: t(
      "pages.dataAnalytics.internalUsage.activity.metrics.hoursInVr",
    ),
    [ActivityLineChartDataKey.UniqueActiveUsers]: t(
      "pages.dataAnalytics.internalUsage.activity.metrics.uniqueActiveUsers",
    ),
  };

  const metricColors: Record<ActivityTrendMetricKey, string> = {
    [ActivityLineChartDataKey.UserSessions]: theme.colors.metrics.userSessions,
    [ActivityLineChartDataKey.HoursInVr]: theme.colors.metrics.hoursInVr,
    [ActivityLineChartDataKey.UniqueActiveUsers]:
      theme.colors.metrics.uniqueActiveUsers,
  };

  const tabs = ACTIVITY_TREND_METRIC_KEYS.map((metric) => ({
    label: metricLabels[metric],
    value: metric,
  }));

  const series = [
    {
      key: activeMetric,
      label: metricLabels[activeMetric],
      color: metricColors[activeMetric],
    },
  ];

  const chartData = useMemo(() => {
    const plotted = plotUnreportedMetricsAtRangeStart(data);

    return isUserLevelDataEnabled
      ? carryForwardActivityTrendGaps(plotted)
      : plotted;
  }, [data, isUserLevelDataEnabled]);

  const tickLabelByDate = useMemo(
    () =>
      new Map(
        data.map((dp) => [
          dp[ActivityLineChartDataKey.Date],
          dp[ActivityLineChartDataKey.TickLabel],
        ]),
      ),
    [data],
  );

  const xTickFormatter = (value: string) => tickLabelByDate.get(value) ?? value;

  const handleTabChange = (_: React.SyntheticEvent, newValue: string) => {
    setActiveMetric(newValue as ActivityTrendMetricKey);
  };

  const handleExportCsv = useCallback(() => {
    // The plotted data, not the raw response, so the export says exactly what
    // the chart shows.
    exportActivityChartCsv(
      chartData,
      isUserLevelDataEnabled,
      {
        period: t("pages.dataAnalytics.internalUsage.exportColumns.period"),
        periodStart: t(
          "pages.dataAnalytics.internalUsage.exportColumns.periodStart",
        ),
        periodEnd: t(
          "pages.dataAnalytics.internalUsage.exportColumns.periodEnd",
        ),
        hoursInVr: t(
          "pages.dataAnalytics.internalUsage.exportColumns.hoursInVr",
        ),
        userSessions: t(
          "pages.dataAnalytics.internalUsage.exportColumns.userSessions",
        ),
        uniqueActiveUsers: t(
          "pages.dataAnalytics.internalUsage.exportColumns.uniqueActiveUsers",
        ),
      },
      `${t("pages.dataAnalytics.internalUsage.activityTrend.exportFilename")}.csv`,
      { startDate, endDate },
      {
        startDate: t(
          "pages.dataAnalytics.internalUsage.exportColumns.dateRangeStart",
        ),
        endDate: t(
          "pages.dataAnalytics.internalUsage.exportColumns.dateRangeEnd",
        ),
      },
      {
        label: t(
          "pages.dataAnalytics.internalUsage.exportColumns.selectedExperiences",
        ),
        names: selectedLicenseNames,
      },
    );
  }, [
    chartData,
    isUserLevelDataEnabled,
    t,
    startDate,
    endDate,
    selectedLicenseNames,
  ]);

  const cardProps = {
    exportAction: {
      label: t("pages.dataAnalytics.internalUsage.exportCsv"),
      onClick: handleExportCsv,
    },
    subtitle: t("pages.dataAnalytics.internalUsage.activityTrend.description"),
    sx: { height: { xs: 444, md: 544 } },
    title: t("pages.dataAnalytics.internalUsage.activityTrend.title"),
  };

  if (isLoading) {
    return (
      <ContainerCard {...cardProps}>
        <DataAnalyticsChartSkeleton
          chartHeight={isMobile ? 200 : 400}
          showFooterRow={false}
          showTopControls
        />
      </ContainerCard>
    );
  }

  return (
    <ContainerCard {...cardProps} isFetching={isFetching}>
      <CustomTabs
        activeTab={activeMetric}
        containerSx={{
          marginBottom: "16px",
          "& .MuiTab-root": { minWidth: { md: TAB_MIN_WIDTH } },
        }}
        onTabChange={handleTabChange}
        tabWidth={{ xs: "fit-content", md: "auto" }}
        tabs={tabs}
      />

      <LineChart
        dashedGaps={!isUserLevelDataEnabled}
        data={chartData}
        hideTooltipWithoutValue={!isUserLevelDataEnabled}
        minYAxisValue={isUserLevelDataEnabled ? 0 : MIN_Y_AXIS_VALUE}
        series={series}
        tooltipLabel={(point: ActivityTrendDataPoint) =>
          t("pages.dataAnalytics.internalUsage.activity.periodRange", {
            start: formatDateYMD(point.periodStart),
            end: formatDateYMD(point.periodEnd),
          })
        }
        xKey={ActivityLineChartDataKey.Date}
        xTickFormatter={xTickFormatter}
      />
    </ContainerCard>
  );
};
